import { __ } from '@/hub-shared/utils/i18n';
import type { RolePermissionMap } from '../types/role-templates';
import manifest from '../../../../../../config/work_permission_ui_manifest.json';
import { PERMISSION_GROUP_LABELS, PERMISSION_LABELS } from './permissionCatalogLabels';

export type ReviewLevel = 'none' | 'review' | 'supervise';

type ManifestEntry = {
  uiKey: string;
  type: 'boolean' | 'review_level';
  keys: string[];
};

type ManifestGroup = {
  groupKey: string;
  entries: ManifestEntry[];
};

type WorkPermissionUiManifest = {
  version: string;
  catalogKeys: string[];
  groups: ManifestGroup[];
  hiddenKeys: Array<{ key: string; reason: string }>;
  roleEditorScopes?: Record<string, { groupKeys: string[]; entryUiKeys: string[] }>;
};

const uiManifest = manifest as WorkPermissionUiManifest;

export type BooleanPermissionEntry = {
  readonly type: 'boolean';
  readonly uiKey: string;
  readonly keys: readonly string[];
  readonly label: string;
  readonly description: string;
};

export type ReviewLevelPermissionEntry = {
  readonly type: 'review_level';
  readonly uiKey: string;
  readonly keys: readonly ['perm.reports.review', 'perm.reports.supervise'];
  readonly label: string;
  readonly description: string;
};

export type PermissionCatalogEntry = BooleanPermissionEntry | ReviewLevelPermissionEntry;

export type PermissionGroup = {
  readonly groupKey: string;
  readonly label: string;
  readonly entries: readonly PermissionCatalogEntry[];
};

export const WORK_PERMISSION_UI_MANIFEST = uiManifest;

export const BASELINE_ONLY_PERMISSION_KEYS = (): readonly string[] =>
  uiManifest.hiddenKeys.map((row) => row.key);

const buildEntry = (entry: ManifestEntry): PermissionCatalogEntry => {
  const labels = PERMISSION_LABELS()[entry.uiKey];
  if (!labels) {
    throw new Error(`Missing permission labels for uiKey: ${entry.uiKey}`);
  }

  if (entry.type === 'review_level') {
    return {
      type: 'review_level',
      uiKey: entry.uiKey,
      keys: ['perm.reports.review', 'perm.reports.supervise'],
      label: labels.label,
      description: labels.description,
    };
  }

  return {
    type: 'boolean',
    uiKey: entry.uiKey,
    keys: entry.keys,
    label: labels.label,
    description: labels.description,
  };
};

export const PERMISSION_GROUPS = (): readonly PermissionGroup[] => {
  const groupLabels = PERMISSION_GROUP_LABELS();

  return uiManifest.groups.map((group) => ({
    groupKey: group.groupKey,
    label: groupLabels[group.groupKey] ?? group.groupKey,
    entries: group.entries.map(buildEntry),
  }));
};

export const PERMISSION_GROUPS_FOR_ROLE = (roleKey: string): readonly PermissionGroup[] => {
  const scope = uiManifest.roleEditorScopes?.[roleKey];
  if (!scope) {
    return PERMISSION_GROUPS();
  }

  const allowedGroups = new Set(scope.groupKeys);
  const allowedEntries = new Set(scope.entryUiKeys);

  return PERMISSION_GROUPS()
    .filter((group) => allowedGroups.has(group.groupKey))
    .map((group) => ({
      ...group,
      entries: group.entries.filter((entry) => allowedEntries.has(entry.uiKey)),
    }))
    .filter((group) => group.entries.length > 0);
};

export const ROLE_DISPLAY_NAMES = (): Record<string, string> => ({
  owner: __('Propriétaire'),
  expert_comptable: __('Expert-comptable'),
  collaborateur_comptable: __('Collaborateur comptable'),
  mission_responsable: __('Responsable de mission'),
  utilisateur_entreprise: __('Utilisateur entreprise'),
  apprenti: __('Apprenti'),
  stagiaire: __('Stagiaire'),
});

export const ROLE_KEY_TO_WP_SLUG: Readonly<Record<string, string>> = {
  owner: 'customer',
  expert_comptable: 'expert_comptable',
  collaborateur_comptable: 'collaborateur_comptable',
  utilisateur_entreprise: 'utilisateur_entreprise',
  apprenti: 'apprenti',
  stagiaire: 'stagiaire',
} as const;

export const readBooleanEntry = (entry: BooleanPermissionEntry, perms: RolePermissionMap): boolean =>
  perms[entry.keys[0]] === true;

export const readReviewLevel = (perms: RolePermissionMap): ReviewLevel => {
  const review = perms['perm.reports.review'] === true;
  const supervise = perms['perm.reports.supervise'] === true;
  if (review && supervise) return 'supervise';
  if (review) return 'review';
  return 'none';
};

export const applyBooleanEntry = (
  entry: BooleanPermissionEntry,
  perms: RolePermissionMap,
  value: boolean,
): RolePermissionMap => {
  const updated = { ...perms };
  for (const key of entry.keys) {
    updated[key] = value;
  }
  return updated;
};

export const applyReviewLevel = (perms: RolePermissionMap, level: ReviewLevel): RolePermissionMap => ({
  ...perms,
  'perm.reports.review': level === 'review' || level === 'supervise',
  'perm.reports.supervise': level === 'supervise',
});

export const REVIEW_LEVEL_OPTIONS = (): Array<{ value: ReviewLevel; label: string }> => [
  { value: 'none', label: __('Non') },
  { value: 'review', label: __('Révision') },
  { value: 'supervise', label: __('Supervision, révision') },
];
